import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import TagsInput from "../TagsInput/TagsInput";
import Rating from "react-rating";
import styles from "./EditListingForm.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../../App";
import RoommateSelector from "../RoommateSelector/RoommateSelector";
import NavBar from "../../components/NavBar/NavBar";

export default function EditListingPage() {
    const {
        register,
        handleSubmit,
        control,
        clearErrors,
        formState: { errors, submitCount },
        reset,
    } = useForm({
        defaultValues: { llRating: 1 },
        mode: "onSubmit",
    });

    const [listing, setListing] = useState(null);

    //the moment we have the fetched data, the form will update (also clean data)
    useEffect(() => {
        if (listing) { 
            console.log("RESETTING WITH LISTING:", listing);
            setExistingImages(listing.image_paths || []); 
            const arrRoommates = Array.isArray(listing.roommates) ? listing.roommates : [];
            const roommates = arrRoommates.map(r => {
                if (typeof r === "string") {
                    const [username, id] = r.split("|");
                    return { username, id };
                }
                return r;
            });
            const actualTags = Array.isArray(listing.tags)
                ? listing.tags.filter(Boolean)
                : typeof listing.tags === "string" && listing.tags.trim() !== ""
                    ? listing.tags.split(",").filter(Boolean)
                    : [];
            reset({
                title: listing.title ?? "",
                rent: listing.rent ?? "",
                addressLine1: listing.addr ?? "",
                addressLine2: "",
                utils: Array.isArray(listing.utils) ? listing.utils : [],
                bed: listing.bed ?? "",
                bath: listing.bath ?? "",
                sqFT: listing.sqFT ?? "",
                description: listing.description ?? "",
                tags: actualTags,
                roommates: Array.isArray(roommates) ? roommates : [],
                llRating: listing.llRating ?? 1,
                comments: listing.comments ?? "",
                image_paths: listing.image_paths?.filter(Boolean) ?? [],
            });
        }
    }, [listing, reset]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const timer = setTimeout(() => {
                clearErrors();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [submitCount, errors, clearErrors]);

    const { id } = useParams(); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${BASE_URL}backend/view_listing.php?id=${id}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setListing(data);
            } catch(error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        if (id) {
            fetchPost();
        }
    }, [id]);

    const navigate = useNavigate();

    const [selectedImages, setSelectedImages] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [deletedImages, setDeletedImages] = useState([]);

    function handleDeleteExisting(url) {
        setExistingImages(prev => prev.filter(path => path !== url));
        setDeletedImages(prev => [...prev, url]);
    }

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setSelectedImages((prev) => [...prev, ...files]);
        }
        e.target.value = "";
    };

    const nonNegativeRule = {
        validate: (value) =>
            Number(value) >= 0 || "Numerical inputs cannot be negative",
    };

    const [flash, setFlash] = useState({ type: "", text: "" });
    const showFlash = (text, type = "error") => setFlash({ type, text });

    useEffect(() => {
        if (flash.text && flash.type === "error") {
            const timer = setTimeout(() => {
                setFlash({ type: "", text: "" }); 
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [flash]);

    const onError = () => {
        showFlash("Please fill out all required fields", "error");
    };

    const preventInvalidNumericInput = (e) => {
        if (["-", "e", "E", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const [isLoggedIn, setIsLoggedIn] = useState(true); 
    useEffect(() => {
        setTimeout(() => {
            fetch(`${BASE_URL}backend/check_session.php`, {
                method: "GET",
                credentials: "include",
            })
            .then(res => res.json())
            .then(data => setIsLoggedIn(data.logged_in));
        }, 50); 
    }, []);

    async function onSubmit(data) {
        const formData = new FormData();
        const totalAfterDeletes = existingImages.length + selectedImages.length;
       
        if (totalAfterDeletes === 0) {
            showFlash("You must have at least one photo before submitting.", "error");
            return;
        }
        if (!isLoggedIn) {
            showFlash("You must log in to submit form.", "error");
            return;
        }
        const address =
            data.addressLine1 + (data.addressLine2 ? " " + data.addressLine2 : "");
        formData.append("addr", address);

        for (const key in data) {
            if (key === "addressLine1" || key === "addressLine2" || key === "images") continue;

            if (key === "roommates") {
                if (Array.isArray(data.roommates)) {
                    data.roommates.forEach((r) => {
                        if (r && typeof r === "object" && "id" in r) {
                            formData.append("roommates[]", `${r.username}|${r.id}`);
                        } else if (typeof r === "string") {
                            formData.append("roommates[]", r.trim());
                        }
                    });
                }
            } else if (key === "utils" || key === "tags") {
                if (Array.isArray(data[key])) {
                    data[key].forEach((val) => {
                        formData.append(`${key}[]`, val);
                    });
                }
            } else {
                formData.append(key, data[key]);
            }
        }

        if (!formData.has("roommates[]")) {
            formData.append("roommates[]", ""); 
        }
        if (!formData.has("tags[]")) {
            formData.append("tags[]", "");
        }
        if (!formData.has("utils[]")) {
            formData.append("utils[]", ""); 
        }

        selectedImages.forEach((file) => {
            formData.append("images[]", file);
        });

        deletedImages.forEach((file) => {
            formData.append("delete_images[]", file);
        });

        formData.append("post_id", id);

        try {
            const res = await fetch(`${BASE_URL}backend/edit_listing.php`, {
                method: "POST",
                body: formData,
                credentials: "include", 
                mode: "cors"
            });

            const ct = res.headers.get("content-type") || "";
            const payload = ct.includes("application/json")
                ? await res.json()
                : { raw: await res.text() };

            if (res.ok && (payload?.success ?? true)) {
                setFlash({ type: "success", text: "Listing updated successfully!" });
                setTimeout(() => navigate("/home"), 400);
            } else {
                showFlash(
                    `Error: ${payload?.error || payload?.message || payload?.raw || "Could not create listing."}`,
                    "error"
                );
            }
        } catch (e) {
            showFlash("Network error. Please try again.", "error");
        }
    }

    if (loading) return <div><NavBar /> Loading...</div>;
    if (error) return <div><NavBar /> Error: {error.message}</div>;
    if (!listing) return <div><NavBar /> No listing found</div>;

    return (
        <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className={styles.component}
            noValidate
        >
            <div className="input-group">
                <label htmlFor="title">Title</label>
                <input
                    id="title"
                    {...register("title", {
                        required: "Title is required",
                        minLength: { value: 3, message: "Title must be at least 3 characters" },
                        maxLength: { value: 100, message: "Title cannot exceed 100 characters" },
                    })}
                    placeholder="Title"
                    aria-invalid={!!errors.title}
                />
                {errors.title && <p className={styles.fieldError}>{errors.title.message}</p>}
            </div>

            <div className="input-group">
                <label htmlFor="rent">Rent</label>
                <input
                    type="number"
                    id="rent"
                    {...register("rent", {
                        required: "Rent is required",
                        min: { value: 1, message: "Rent must be at least $1" },
                        ...nonNegativeRule,
                    })}
                    onKeyDown={preventInvalidNumericInput}
                    onInput={(e) => {
                        if (e.target.value < 0) e.target.value = 0;
                    }}
                    placeholder="$/month"
                    aria-invalid={!!errors.rent}
                />
                {errors.rent && <p className={styles.fieldError}>{errors.rent.message}</p>}
            </div>

            <div className="input-group">
                <label htmlFor="address1">Address</label>
                <input
                    id="address1"
                    {...register("addressLine1", {
                        required: "Street Address is required",
                        maxLength: { value: 100, message: "Address cannot exceed 100 characters" },
                    })}
                    placeholder="Street Address"
                    aria-invalid={!!errors.addressLine1}
                />
            </div>
            
            <div className="input-group">
                <input
                    id="address2"
                    {...register("addressLine2", {
                        maxLength: { value: 100, message: "Address cannot exceed 100 characters" },
                    })}
                    placeholder="Apt/Suite"
                    aria-invalid={!!errors.addressLine2}
                />
                {errors.addressLine1 && (
                    <p className={styles.fieldError}>{errors.addressLine1.message}</p>
                )}
                {errors.addressLine2 && (
                    <p className={styles.fieldError}>{errors.addressLine2.message}</p>
                )}
            </div>
            
            <div className="input-group">
                <label htmlFor="existing">Existing Photos</label>
                {existingImages.length > 0 && (
                    <ul className={styles.fileList} id="existing">
                        {existingImages.map((url, idx) => (
                        <li key={idx} className={styles.fileItem}>
                            <span>{url.split("/").pop()}</span>
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => handleDeleteExisting(url)}
                                >
                                Remove
                            </button>
                        </li>
                        ))}
                    </ul>
                )}
                <label htmlFor="images">Upload Photo</label>
                <input
                    type="file"
                    id="images"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    onChange={handleFileChange}
                />

                {selectedImages.length > 0 && (
                    <ul className={styles.fileList}>
                        {selectedImages.map((file, idx) => (
                            <li key={idx} className={styles.fileItem}>
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() =>
                                        setSelectedImages((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {errors.images && <p className={styles.fieldError}>{errors.images.message}</p>}
            </div>

            <div className={`${styles.formGroup} ${styles.utilitiesGroup}`}>
                <label>Utilities/Amenities Included</label>
                <div>
                    <div>
                        <input type="checkbox" id="water" value="water" {...register("utils")} />
                        <label htmlFor="water" className={styles.checkboxLabel}>
                            Water
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="parking"
                            value="parking"
                            {...register("utils")}
                        />
                        <label htmlFor="parking" className={styles.checkboxLabel}>
                            Parking
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="electricity"
                            value="electricity"
                            {...register("utils")}
                        />
                        <label htmlFor="electricity" className={styles.checkboxLabel}>
                            Electricity
                        </label>
                    </div>

                    <div>
                        <input type="checkbox" id="gas" value="gas" {...register("utils")} />
                        <label htmlFor="gas" className={styles.checkboxLabel}>
                            Gas
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="laundry"
                            value="laundry"
                            {...register("utils")}
                        />
                        <label htmlFor="laundry" className={styles.checkboxLabel}>
                            Laundry
                        </label>
                    </div>

                    <div>
                        <input type="checkbox" id="trash" value="trash" {...register("utils")} />
                        <label htmlFor="trash" className={styles.checkboxLabel}>
                            Trash Collection
                        </label>
                    </div>
                </div>
            </div>

            {/* Row 1: Bedrooms and Bathrooms */}
            <div className={styles.row}>
                <div className={`${styles.half} input-group`}>
                    <label htmlFor="bedrooms"># Bedrooms</label>
                    <input
                        type="number"
                        id="bedrooms"
                        {...register("bed", {
                            required: "# Bedrooms is required",
                            min: { value: 1, message: "# Bedrooms must be at least 1" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="# Bedrooms"
                        aria-invalid={!!errors.bed}
                    />
                    {errors.bed && <p className={styles.fieldError}>{errors.bed.message}</p>}
                </div>

                <div className={`${styles.half} input-group`}>
                    <label htmlFor="bathrooms"># Bathrooms</label>
                    <input
                        type="number"
                        id="bathrooms"
                        {...register("bath", {
                            required: "# Bathrooms is required",
                            min: { value: 1, message: "# Bathrooms must be at least 1" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="# Bathrooms"
                        aria-invalid={!!errors.bath}
                    />
                    {errors.bath && <p className={styles.fieldError}>{errors.bath.message}</p>}
                </div>
            </div>

            {/* Row 2: Size (and empty space) */}
            <div className={styles.row}>
                <div className={`${styles.half} input-group`}>
                    <label htmlFor="square_foot">Size (sqft)</label>
                    <input
                        type="number"
                        id="square_foot"
                        {...register("sqFT", {
                            required: "Size is required",
                            min: { value: 1, message: "Size must be at least 1 sqft" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="Size in sqft"
                        aria-invalid={!!errors.sqFT}
                    />
                    {errors.sqFT && <p className={styles.fieldError}>{errors.sqFT.message}</p>}
                </div>
                <div className={styles.half}></div>
            </div>

            <div className="input-group">
                <label htmlFor="description">Description</label>
                <textarea
                    id="description"
                    className={styles.descriptionTextarea}
                    {...register("description", {
                        required: "Description is required",
                        maxLength: { value: 1000, message: "Description cannot exceed 1000 characters" },
                    })}
                    placeholder="Describe the neighborhood, commute, roommates, utilities, etc."
                    aria-invalid={!!errors.description}
                    rows={8}
                    style={{ resize: "vertical" }}
                />
                {errors.description && (
                    <p className={styles.fieldError}>{errors.description.message}</p>
                )}
            </div>

            <div className="input-group">
                <TagsInput control={control} name="tags" label="Tags (e.g., vegetarian, dog, cat)" />
            </div>

            <div className="input-group">
                <Controller
                    name="roommates"
                    control={control}
                    render={({ field }) => (
                        <RoommateSelector value={field.value || []} onChange={field.onChange} />
                    )}
                />
            </div>
            
            <div className={styles.ratingGroup}>
                <Controller
                    name="llRating"
                    control={control}
                    rules={{
                        required: "Roommates rating is required",
                        validate: (v) => v > 0 || "Roommates rating is required",
                    }}
                    render={({ field }) => (
                        <div>
                            <label>Roommates Rating</label>
                            <br />
                            <Rating
                                emptySymbol={<span style={{ fontSize: "2rem", color: "#ccc" }}>☆</span>}
                                fullSymbol={<span style={{ fontSize: "2rem", color: "#EC221F" }}>★</span>}
                                initialRating={field.value}
                                onClick={(rate) => field.onChange(rate)}
                            />
                            {errors.llRating && (
                                <p className={styles.fieldError}>{errors.llRating.message}</p>
                            )}
                        </div>
                    )}
                />

                <div className="input-group">
                    <label htmlFor="comments">Comments</label>
                    <textarea
                        id="comments"
                        className={styles.commentsTextarea}
                        {...register("comments", {
                            maxLength: { value: 500, message: "Comments cannot exceed 500 characters" },
                        })}
                        placeholder="Landlord comments"
                        aria-invalid={!!errors.comments}
                        rows={4}
                        style={{ resize: "vertical" }}
                    />
                    {errors.comments && (
                        <p className={styles.fieldError}>{errors.comments.message}</p>
                    )}
                </div>
            </div>

            {flash.text && (
                <div
                    className={flash.type === "success" ? styles.alertSuccess : styles.alertError}
                    role="status"
                    aria-live="polite"
                >
                    {flash.text}
                </div>
            )}

            <div className={styles.formActions}>
                <button type="submit">Submit</button>
            </div>

        </form>
    );
}